import { AttributeType } from "@evergis/api";
import React, { FC, memo, useMemo } from "react";
import { Dropdown, Input } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { fieldTypes, getFieldTypeValue } from "evergis/core/attributes/fieldTypes";
import { AttributeTypeSelect } from "../../NewLayer/styled";
import { useAttributeUpdate } from "../hooks/useAttributeUpdate";
import { AttributeCheckbox, AttributesContainer } from "../styled";
import { useAttributesError } from "../../../../hooks/useAttributesError";
import { ImportAttributeProps } from "../types";

const EXCLUDE_IMPORT_TYPES = [AttributeType.Point, AttributeType.MultiPolygon];

const ImportAttributeItemComponent: FC<ImportAttributeProps> = ({
  idField,
  idAttribute,
  geometryAttribute,
  updateAttribute,
  attribute,
  error,
  firstRowValue,
}) => {
  const { t } = useTranslation();
  const { error: attributesError } = useAttributesError(error);
  const { name, alias = "", disabled, id, type, subType, expression } = attribute;
  const isIdField = name === idField;
  const isChecked = !disabled;
  const isSystemAttribute = [idAttribute, geometryAttribute].includes(name);
  const { changeDisabled, changeName, changeAlias, changeType } = useAttributeUpdate(id, updateAttribute);
  const importFieldTypes = useMemo(
    () =>
      fieldTypes
        .map(value => ({
          ...value,
          text: value.langKey ? t(value.langKey, { ns: "common" }) : value.text,
        }))
        .filter(({ value }) => !EXCLUDE_IMPORT_TYPES.includes(value as AttributeType)),
    [t],
  );

  return (
    <AttributesContainer>
      <AttributeCheckbox
        checked={isIdField || isChecked || !!expression || isSystemAttribute}
        disabled={(isChecked && isIdField) || !!expression || isSystemAttribute}
        onChange={changeDisabled}
      />
      <Input
        label={t("import.fieldName", { ns: "dialogs" })}
        value={name}
        disabled={isIdField || !isChecked || !!expression || isSystemAttribute}
        width={"14rem"}
        onChange={changeName}
        error={!!error}
        help={attributesError}
      />
      <Input
        label={t("import.fieldAlias", { ns: "dialogs" })}
        value={alias}
        disabled={!isChecked || !!expression || isSystemAttribute}
        help={t("import.anyCharacters", { ns: "dialogs" })}
        width={"14rem"}
        onChange={changeAlias}
      />
      <AttributeTypeSelect>
        <Dropdown
          value={getFieldTypeValue(type, subType)}
          label={t("import.fieldType", { ns: "dialogs" })}
          zIndex={120}
          width={"16.375rem"}
          options={importFieldTypes}
          disabled={isIdField || !isChecked || !!expression || isSystemAttribute}
          onChange={changeType}
          help={firstRowValue}
        />
      </AttributeTypeSelect>
    </AttributesContainer>
  );
};

export const ImportAttributeItem = memo(ImportAttributeItemComponent);
